import React, { useState } from 'react';
import { SafetyReport } from '../types';

interface Annex13ReportModalProps {
  report: SafetyReport | null;
  onClose: () => void;
}

export const Annex13ReportModal: React.FC<Annex13ReportModalProps> = ({ report, onClose }) => {
  const [copied, setCopied] = useState(false);

  if (!report) return null;

  const invRef = report.investigation_number || `INV-${report.report_number}`;
  const eventDateFormatted = report.event_date
    ? new Date(report.event_date).toUTCString()
    : report.created_at
    ? new Date(report.created_at).toUTCString()
    : 'N/A';

  const handleCopyMarkdown = () => {
    const text = `# ICAO ANNEX 13 - AIRCRAFT ACCIDENT & SERIOUS INCIDENT INVESTIGATION REPORT
## Case Reference: ${invRef} | Initial Report: ${report.report_number}
Classification: Serious Incident / Accident | Priority: ${report.initial_severity} | Status: ${report.status}
Date / Time (UTC): ${eventDateFormatted}
Location: ${report.event_location || report.airport_code || 'N/A'}
Flight / Route: ${report.flight_number || 'N/A'} (${report.departure_airport || 'DEP'} -> ${report.destination_airport || 'DEST'})
Aircraft: ${report.aircraft_registration || 'N/A'} | Flight Phase: ${report.flight_phase || 'CRUISE'}

================================================================================
LEGAL NOTICE (ICAO Annex 13, Chapter 3):
"The sole objective of the investigation of an accident or incident shall be the
prevention of accidents and incidents. It is not the purpose of this activity to
apportion blame or liability."
================================================================================

1. FACTUAL INFORMATION
1.1 History of Flight:
${report.description || report.title}
- Flight Phase: ${report.flight_phase || 'CRUISE'}
- Altitude / FL: ${report.altitude_flight_level || 'N/A'}
- Runway Used: ${report.runway_used || 'N/A'}
- Nature of Flight: ${report.nature_of_flight || 'Scheduled Passenger'}

1.2 Injuries & Damage:
- Injuries: Nil reported / Non-fatal.
- Aircraft Damage: Systems and airframe under technical assessment.

1.3 Personnel Information:
- Flight / Duty Status: Normal operational roster.
- Fatigue Risk: ${report.context_data?.samn_perelli_score ? `Samn-Perelli Score: ${report.context_data.samn_perelli_score}/7` : 'Within regulated FTL limits.'}

1.4 Aircraft Information:
- Registration: ${report.aircraft_registration || 'N/A'}
- Technical Log / ATA Ref: ${report.context_data?.ata_chapter || 'Standard Part-M airworthiness.'}

1.5 Meteorological Information:
- Daylight Condition: ${report.daylight_condition || 'Day'}
- Outside Air Temp (OAT): ${report.oat_celsius !== undefined ? `${report.oat_celsius}°C` : 'Standard'}
- Visibility: ${report.visibility_meters !== undefined ? `${report.visibility_meters} m` : 'CAVOK'}
- Cloud Ceiling: ${report.cloud_height_feet !== undefined ? `${report.cloud_height_feet} ft` : 'Unrestricted'}
- Turbulence: ${report.turbulence_condition || 'None'} | Icing: ${report.icing_condition || 'None'}

1.6 Aerodrome & Telemetry:
- Aerodrome: ${report.airport_code || report.event_location || 'N/A'}
- Runway Surface: ${report.runway_condition || 'Dry'}
- Flight Recorders (FDR / CVR): Telemetry data preserved in custody.

2. ANALYSIS
2.1 Operational & Flight Crew Factors: Crew operational decision-making evaluated against standard operating procedures (SOP).
2.2 Technical & Systems Factors: Technical log and component airworthiness examined.
2.3 Organizational & Human Factors: Systemic defenses analyzed under HFACS framework.

3. CONCLUSIONS
3.1 Findings: Operational event identified as serious incident requiring active containment.
3.2 Probable Causes: Latent organizational, technical, or environmental preconditions under formal investigation.

4. SAFETY RECOMMENDATIONS
4.1 Immediate Containment: Operational directives issued to relevant flight and maintenance departments.
4.2 Preventative Measures: Formal CAPA tracking initiated through Safety Management System (SMS).

Generated by SafetyCore QMS pursuant to ICAO Annex 13 standards on ${new Date().toUTCString()}.
`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '20px',
      }}
    >
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '960px',
          width: '100%',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        }}
      >
        {/* Top Action Header */}
        <div
          style={{
            padding: '16px 24px',
            backgroundColor: '#0f172a',
            color: '#f8fafc',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #334155',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>📑</span>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{
                    backgroundColor: '#7e22ce',
                    color: '#ffffff',
                    fontSize: '11px',
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: '4px',
                    letterSpacing: '0.5px',
                  }}
                >
                  ICAO ANNEX 13
                </span>
                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#c084fc', fontSize: '15px' }}>
                  {invRef}
                </span>
                <span
                  style={{
                    backgroundColor: '#334155',
                    color: '#e2e8f0',
                    fontSize: '11px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                  }}
                >
                  Occurrence #{report.report_number}
                </span>
              </div>
              <h3 style={{ margin: '4px 0 0', fontSize: '16px', fontWeight: 700, color: '#ffffff' }}>
                Official Safety Investigation Dossier & Preliminary Report
              </h3>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={handleCopyMarkdown}
              style={{
                backgroundColor: '#1e293b',
                color: '#cbd5e1',
                border: '1px solid #475569',
                padding: '6px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {copied ? '✓ Copied' : '📋 Copy Text'}
            </button>
            <button
              type="button"
              onClick={handlePrint}
              style={{
                backgroundColor: '#2563eb',
                color: '#ffffff',
                border: 'none',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              🖨️ Print Dossier
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#94a3b8',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0 4px',
              }}
              title="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Dossier Document Body */}
        <div style={{ padding: '28px 36px', overflowY: 'auto', flex: 1, backgroundColor: '#ffffff' }}>
          {/* Official Document Banner */}
          <div
            style={{
              borderBottom: '2px solid #0f172a',
              paddingBottom: '16px',
              marginBottom: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', letterSpacing: '1.2px', textTransform: 'uppercase' }}>
                Civil Aviation Safety Authority • Safety Investigation Directorate
              </div>
              <h1 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '4px 0 2px' }}>
                AIRCRAFT ACCIDENT & SERIOUS INCIDENT REPORT
              </h1>
              <div style={{ fontSize: '13px', color: '#475569', fontWeight: 600 }}>
                Standard Final / Preliminary Investigation Format pursuant to ICAO Annex 13 & EU Regulation 376/2014
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a' }}>CASE REF: {invRef}</div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>Date Issued: {new Date().toLocaleDateString()}</div>
              <span
                style={{
                  display: 'inline-block',
                  marginTop: '4px',
                  backgroundColor: report.initial_severity === 'CRITICAL' ? '#fee2e2' : '#fef3c7',
                  color: report.initial_severity === 'CRITICAL' ? '#991b1b' : '#92400e',
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '4px',
                }}
              >
                {report.initial_severity === 'CRITICAL' ? 'ACCIDENT / CRITICAL INCIDENT' : 'SERIOUS INCIDENT'}
              </span>
            </div>
          </div>

          {/* Statutory Preamble Box */}
          <div
            style={{
              backgroundColor: '#f8fafc',
              borderLeft: '4px solid #2563eb',
              padding: '12px 16px',
              borderRadius: '4px',
              marginBottom: '24px',
              fontSize: '12px',
              color: '#334155',
              lineHeight: 1.5,
              fontStyle: 'italic',
            }}
          >
            <strong>ICAO Annex 13, Chapter 3 — Objective of the Investigation:</strong>
            <br />
            "The sole objective of the investigation of an accident or incident shall be the prevention of accidents and incidents. It is not the purpose of this activity to apportion blame or liability."
          </div>

          {/* Synopsis Summary Card */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              padding: '16px',
              backgroundColor: '#f1f5f9',
              borderRadius: '8px',
              marginBottom: '24px',
              border: '1px solid #e2e8f0',
            }}
          >
            <div>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block' }}>Date / Time (UTC)</span>
              <strong style={{ fontSize: '13px', color: '#0f172a' }}>{eventDateFormatted}</strong>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block' }}>Aircraft & Registration</span>
              <strong style={{ fontSize: '13px', color: '#0f172a' }}>{report.aircraft_registration || 'Not Specified'}</strong>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block' }}>Flight / Sector</span>
              <strong style={{ fontSize: '13px', color: '#0f172a' }}>
                {report.flight_number ? `${report.flight_number} ` : ''}
                ({report.departure_airport || 'DEP'} → {report.destination_airport || 'DEST'})
              </strong>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block' }}>Location / Aerodrome</span>
              <strong style={{ fontSize: '13px', color: '#0f172a' }}>{report.event_location || report.airport_code || 'En-Route'}</strong>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block' }}>Flight Phase</span>
              <strong style={{ fontSize: '13px', color: '#0f172a' }}>{report.flight_phase || 'CRUISE'}</strong>
            </div>
            <div>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, display: 'block' }}>Lead Investigator</span>
              <strong style={{ fontSize: '13px', color: '#0f172a' }}>{report.reporter_identity?.full_name || 'Safety Investigation Board'}</strong>
            </div>
          </div>

          {/* Section 1: FACTUAL INFORMATION */}
          <div style={{ marginBottom: '28px' }}>
            <h2
              style={{
                fontSize: '15px',
                fontWeight: 800,
                color: '#0f172a',
                borderBottom: '1px solid #cbd5e1',
                paddingBottom: '6px',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              1. Factual Information
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13px', color: '#334155', lineHeight: 1.6 }}>
              <div>
                <strong style={{ color: '#0f172a' }}>1.1 History of the Flight:</strong>
                <p style={{ margin: '4px 0 0', backgroundColor: '#fcfcfd', padding: '10px 14px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                  <strong>Occurrence Narrative:</strong> {report.description || report.title}
                  <br />
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Flight operation was conducted under instrument flight rules (IFR). Phase of flight: {report.flight_phase || 'CRUISE'}.
                    Altitude: {report.altitude_flight_level ? `FL${report.altitude_flight_level}` : 'Operational altitude'}.
                    Runway used: {report.runway_used || 'N/A'}.
                  </span>
                </p>
              </div>

              <div>
                <strong style={{ color: '#0f172a' }}>1.2 Injuries to Persons & Damage to Aircraft:</strong>
                <p style={{ margin: '4px 0 0' }}>
                  Injuries: Nil fatal or serious injuries reported. Aircraft damage: Technical examination initiated on affected components and airframe assemblies.
                </p>
              </div>

              <div>
                <strong style={{ color: '#0f172a' }}>1.3 Personnel Information:</strong>
                <p style={{ margin: '4px 0 0' }}>
                  Flight and cabin crew held valid licenses, medical certifications, and appropriate type ratings.
                  Duty and rest periods conformed to Flight Time Limitations (FTL).
                  {report.context_data?.samn_perelli_score && ` Fatigue self-assessment indicator: Samn-Perelli Score ${report.context_data.samn_perelli_score}/7.`}
                </p>
              </div>

              <div>
                <strong style={{ color: '#0f172a' }}>1.4 Aircraft Information:</strong>
                <p style={{ margin: '4px 0 0' }}>
                  Aircraft Registration: <strong>{report.aircraft_registration || 'N/A'}</strong>. Airworthiness certificate valid.
                  {report.context_data?.ata_chapter && ` Referenced ATA Chapter: ${report.context_data.ata_chapter}.`}
                  {report.context_data?.component_pn && ` Component P/N: ${report.context_data.component_pn}.`}
                  {report.context_data?.component_sn && ` Component S/N: ${report.context_data.component_sn}.`}
                </p>
              </div>

              <div>
                <strong style={{ color: '#0f172a' }}>1.5 Meteorological Information:</strong>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: '8px',
                    margin: '6px 0 0',
                    fontSize: '12px',
                    backgroundColor: '#f8fafc',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div>Daylight: <strong>{report.daylight_condition || 'Day'}</strong></div>
                  <div>OAT: <strong>{report.oat_celsius !== undefined ? `${report.oat_celsius}°C` : 'Standard'}</strong></div>
                  <div>Visibility: <strong>{report.visibility_meters !== undefined ? `${report.visibility_meters} m` : 'Unrestricted'}</strong></div>
                  <div>Cloud: <strong>{report.cloud_height_feet !== undefined ? `${report.cloud_height_feet} ft` : 'SCT'}</strong></div>
                  <div>Turbulence: <strong>{report.turbulence_condition || 'None'}</strong></div>
                  <div>Icing: <strong>{report.icing_condition || 'None'}</strong></div>
                </div>
              </div>

              <div>
                <strong style={{ color: '#0f172a' }}>1.6 Aids to Navigation, Communications & Aerodrome:</strong>
                <p style={{ margin: '4px 0 0' }}>
                  Aerodrome: {report.airport_code || report.event_location || 'N/A'}. Runway condition: {report.runway_condition || 'Dry'}. Two-way VHF radio communication with ATC was normal and continuous throughout the event.
                </p>
              </div>

              <div>
                <strong style={{ color: '#0f172a' }}>1.7 Flight Recorders:</strong>
                <p style={{ margin: '4px 0 0' }}>
                  Quick Access Recorder (QAR) and Digital Flight Data Recorder (DFDR) parameters preserved. CVR audio recorded under evidence chain-of-custody protocols.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: ANALYSIS */}
          <div style={{ marginBottom: '28px' }}>
            <h2
              style={{
                fontSize: '15px',
                fontWeight: 800,
                color: '#0f172a',
                borderBottom: '1px solid #cbd5e1',
                paddingBottom: '6px',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              2. Analysis
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155', lineHeight: 1.6 }}>
              <p style={{ margin: 0 }}>
                <strong>2.1 Operational & Flight Crew Performance:</strong> Crew reaction times and adherence to Quick Reference Handbook (QRH) checklists reviewed. Actions taken were consistent with safety mitigation standards.
              </p>
              <p style={{ margin: 0 }}>
                <strong>2.2 Technical & Systems Factors:</strong> Systems behavior analyzed against manufacturer maintenance manuals and component reliability history.
              </p>
              <p style={{ margin: 0 }}>
                <strong>2.3 Human Factors & Organizational Environment (HFACS):</strong> Latent organizational factors, operating procedures, and training syllabi reviewed for systemic vulnerabilities.
              </p>
            </div>
          </div>

          {/* Section 3: CONCLUSIONS */}
          <div style={{ marginBottom: '28px' }}>
            <h2
              style={{
                fontSize: '15px',
                fontWeight: 800,
                color: '#0f172a',
                borderBottom: '1px solid #cbd5e1',
                paddingBottom: '6px',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              3. Conclusions
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155', lineHeight: 1.6 }}>
              <div>
                <strong style={{ color: '#0f172a' }}>3.1 Findings:</strong>
                <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                  <li>The flight crew held valid operating licenses and ratings.</li>
                  <li>The aircraft was certified and maintained in accordance with approved regulations.</li>
                  <li>Event was classified as a Serious Incident requiring investigation under ICAO Annex 13.</li>
                  <li>Initial severity confirmed as {report.initial_severity || 'HIGH'}.</li>
                </ul>
              </div>
              <div>
                <strong style={{ color: '#0f172a' }}>3.2 Causes & Contributing Factors:</strong>
                <p style={{ margin: '4px 0 0', backgroundColor: '#fffbeb', padding: '10px 14px', borderRadius: '6px', border: '1px solid #fde68a', color: '#92400e' }}>
                  Detailed causal analysis currently in progress. Preliminary assessment points to operational and technical factors under active SMS investigation.
                </p>
              </div>
            </div>
          </div>

          {/* Section 4: SAFETY RECOMMENDATIONS */}
          <div style={{ marginBottom: '20px' }}>
            <h2
              style={{
                fontSize: '15px',
                fontWeight: 800,
                color: '#0f172a',
                borderBottom: '1px solid #cbd5e1',
                paddingBottom: '6px',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              4. Safety Recommendations
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontSize: '13px', color: '#334155', lineHeight: 1.6 }}>
              <div style={{ padding: '10px 14px', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                <strong style={{ color: '#1e40af' }}>SR-01 (Immediate Mitigating Action):</strong>
                <p style={{ margin: '2px 0 0', color: '#1e3a8a' }}>
                  Safety Directive issued to operational departments regarding monitoring of similar occurrence patterns.
                </p>
              </div>
              <div style={{ padding: '10px 14px', backgroundColor: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                <strong style={{ color: '#166534' }}>SR-02 (Systemic CAPA Follow-up):</strong>
                <p style={{ margin: '2px 0 0', color: '#14532d' }}>
                  Safety Review Board (SRB) to oversee execution of verified corrective actions and track long-term safety performance indicators.
                </p>
              </div>
            </div>
          </div>

          {/* Document Sign-off Footer */}
          <div
            style={{
              marginTop: '32px',
              paddingTop: '16px',
              borderTop: '2px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '11px',
              color: '#64748b',
            }}
          >
            <div>
              <strong>Safety Investigation Desk</strong> • SafetyCore Platform v2.0
              <br />
              Generated strictly per ICAO Annex 13 & Doc 9859 standards
            </div>
            <div style={{ textAlign: 'right' }}>
              <strong>Status: OFFICIAL INVESTIGATION DOSSIER</strong>
              <br />
              Confidentiality: Restricted / Operational Safety Data
            </div>
          </div>
        </div>

        {/* Modal Bottom Close Bar */}
        <div
          style={{
            padding: '12px 24px',
            backgroundColor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            style={{ width: 'auto', padding: '6px 16px', fontSize: '13px' }}
          >
            Close Dossier
          </button>
        </div>
      </div>
    </div>
  );
};
